import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    NumberField,
    FunctionField,
    EditButton,
    ShowButton,
    DeleteButton,
    Filter,
    TextInput,
    SearchInput,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    useTranslate,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import {
    Box,
    Chip,
    Typography,
    Tooltip,
    Fab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Rule, Add as AddIcon, Star } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PolicyFilter = (props: any) => {
    const translate = useTranslate();
    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.reservation-policies.list.filters.search')}
                source="search"
                alwaysOn
            />
        </Filter>
    );
};

const MobilePolicyFilter = (props: any) => (
    <Filter {...props}>
        <SearchInput source="search" alwaysOn />
    </Filter>
);

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0, gap: 1 }}>
        <ExportButton />
        <RefreshButton />
    </Box>
);

const FeePolicyChip = ({ value }: { value: string }) => {
    const translate = useTranslate();
    if (!value) return <span>-</span>;
    const colorMap: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error'> = {
        none: 'default',
        hold_release: 'success',
        hold_apply: 'primary',
        hold_forfeit_on_noshow: 'warning',
    };
    return (
        <Tooltip title={translate(`resources.reservation-policies.feePolicyTooltips.${value}`, { _: '' })}>
            <Chip
                size="small"
                label={translate(`resources.reservation-policies.feePolicies.${value}`, { _: value })}
                color={colorMap[value] || 'default'}
            />
        </Tooltip>
    );
};

const SystemDefaultChip = ({ value }: { value: boolean }) => {
    const translate = useTranslate();
    if (!value) return null;
    return (
        <Chip
            size="small"
            color="warning"
            icon={<Star fontSize="small" />}
            label={translate('resources.reservation-policies.fields.isSystemDefault')}
        />
    );
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/reservation-policies/${record.id}/show`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Rule fontSize="small" color="primary" />
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {record.name || record.id}
                            </Typography>
                            {(record.isSystemDefault) && (
                                <Chip size="small" color="warning" icon={<Star fontSize="small" />} label={translate('resources.reservation-policies.fields.isSystemDefault')} />
                            )}
                        </Box>
                        {(record.description) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {record.description}
                            </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <FeePolicyChip value={record.feePolicy} />
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const PolicyList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={isMobile ? <MobilePolicyFilter /> : <PolicyFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                sort={{ field: 'is_system_default', order: 'DESC' }}
                perPage={25}
                empty={isMobile ? false : undefined}
                title={translate('resources.reservation-policies.list.title')}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        rowClick="show"
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                        }}
                    >
                        <TextField
                            source="name"
                            label={translate('resources.reservation-policies.fields.name')}
                        />
                        <TextField
                            source="description"
                            label={translate('resources.reservation-policies.fields.description')}
                            sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                        />
                        <FunctionField
                            label={translate('resources.reservation-policies.fields.reservationMode')}
                            render={(record: any) => {
                                const m = record.reservationMode;
                                if (!m) return '-';
                                return (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={translate(`resources.reservation-policies.modes.${m}`, { _: m })}
                                    />
                                );
                            }}
                        />
                        <FunctionField
                            label={translate('resources.reservation-policies.fields.accessMode')}
                            render={(record: any) => {
                                const m = record.accessMode;
                                if (!m) return '-';
                                return (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={translate(`resources.reservation-policies.accessModes.${m}`, { _: m })}
                                    />
                                );
                            }}
                        />
                        <FunctionField
                            label={translate('resources.reservation-policies.fields.feePolicy')}
                            render={(record: any) => <FeePolicyChip value={record.feePolicy} />}
                        />
                        <NumberField source="maxActivePerUser" label={translate('resources.reservation-policies.fields.maxActivePerUser')} />
                        <FunctionField
                            label={translate('resources.reservation-policies.fields.isSystemDefault')}
                            render={(record: any) => <SystemDefaultChip value={!!record.isSystemDefault} />}
                            source="isSystemDefault"
                        />
                        <FunctionField
                            label=""
                            render={(record: any) => {
                                const isDefault = !!(record.isSystemDefault);
                                return (
                                    <Box display="flex" gap={0}>
                                        <Tooltip title={translate('ra.action.show')}>
                                            <span><ShowButton record={record} size="small" label="" /></span>
                                        </Tooltip>
                                        <Tooltip title={translate('ra.action.edit')}>
                                            <span><EditButton record={record} size="small" label="" /></span>
                                        </Tooltip>
                                        {!isDefault && (
                                            <Tooltip title={translate('ra.action.delete')}>
                                                <span><DeleteButton record={record} size="small" label="" /></span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                );
                            }}
                            source="actions"
                            textAlign="right"
                        />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/reservation-policies/create')}
                    sx={{ position: 'fixed', bottom: 24, right: 24, backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007A5E' }, zIndex: 1100 }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};

export default PolicyList;
